/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Polling, DownloadService, HttpService, PollingService } from '@dm/core';
import { type Observable } from 'rxjs';

// 批量配置操作类型
export enum OperatortypeE {
    Start = 0,  // 开始执行
    Stop = 1,   // 停止执行
    Continue = 2 // 继续执行
}

export enum RunningStatue {
    initial = 0,
    loading = 1,
    checkFailed = 2,
    notExecute = 3,
    executing = 4,
    stopInFailed = 7
}

@Injectable()
export class BatchConfigService {
    // http请求公共配置
    options: BaseRequestOptionsArgs = {
        mask: false,
        doException: true,
    };

    constructor(
        private httpService: HttpService,
        private downloadService: DownloadService,
        private polling: PollingService,
    ) { }

    /**
     * 获取上传URL
     *
     * @returns
     * @memberof BatchConfigService
     */
    getCliUploadUrl(): string {
        const rest = this.httpService.makeUrl('buildrun.uploadTemplate');
        return rest.url;
    }

    /**
     * 下载CLI模板文件
     *
     * @returns
     * @memberof BatchConfigService
     */
    downloadFile(): Observable<boolean> {
        const rest = this.httpService.makeUrl('buildrun.downloadExample');
        const cliTemplateFileName = 'Example.conf';
        return this.downloadService.download(rest, cliTemplateFileName);
    }

    /**
     * 手动删除批量配置
     */
    clearConfig(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('buildrun.clearConfig');
        return this.httpService.put(rest, undefined, { mask: true, doSuccess: true });
    }

    /**
     * 获取执行状态
     *
     * @returns
     * @memberof BatchConfigService
     */
    getExecutionStatus(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('buildrun.getExecutionStatus');
        return this.httpService.get(rest, {
            mask: false,
            doException: true,
        });
    }

    /**
     * 状态轮询
     *
     * @returns
     * @memberof BatchConfigService
     */
    updatingExecutionStatus(): Polling {
        const rest = this.httpService.makeUrl('buildrun.getExecutionStatus');
        return this.polling.add(rest, 1);
    }

    /**
     * 中止轮询
     *
     * @param {Polling} p
     * @memberof BatchConfigService
     */
    stopPolling(p: Polling): void {
        this.polling.remove(p);
    }

    /**
     * 执行CLI批量配置
     *
     * @returns
     * @memberof BatchConfigService
     */
    executeCli(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('buildrun.executeCli');
        const params = {
            Operatortype: OperatortypeE.Start,
        };
        return this.httpService.post(rest, params, this.options);
    }

    /**
     * 停止执行CLI批量配置
     *
     * @returns
     * @memberof BatchConfigService
     */
    stopExecuteCli(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('buildrun.stopExecuteCli');
        const params = {
            Operatortype: OperatortypeE.Stop,
        };
        return this.httpService.post(rest, params, this.options);
    }

    /**
     * 继续执行CLI批量配置
     *
     * @returns
     * @memberof BatchConfigService
     */
    continueExecuteCli(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('buildrun.continueExecuteCli');
        const params = {
            Operatortype: OperatortypeE.Continue,
        };
        return this.httpService.post(rest, params, this.options);
    }
}
